import { type JSX, useId } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { Card } from '../primitives/Card.js';
import { Dialog } from '../primitives/Dialog.js';
import { CloseIcon } from '../shell/icons.js';
import { useOptionalAnalyticsConsent } from './AnalyticsConsentProvider.js';
import { ConsentSaveError } from './ConsentSaveError.js';

const noticeClasses = 'mx-4 mt-4';
const actionClasses = 'flex flex-wrap gap-3';
const closeButtonClasses =
  'ml-4 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-foreground hover:bg-muted';

/**
 * Optional initial choice in a focus-managed bottom dialog. Closing it means
 * “not now”: it records no choice, leaves capture off, and is remembered only
 * in the mounted consent provider for this account and notice version.
 */
export function AnalyticsConsentNotice(): JSX.Element | null {
  const t = useTranslate();
  const consent = useOptionalAnalyticsConsent();
  const headingId = useId();

  if (consent === null) {
    return null;
  }

  const {
    resolution,
    savingChoice,
    saveError,
    choose,
    dismissInitialNotice,
    initialNoticeDismissed,
  } = consent;
  if (resolution.kind === 'unavailable') {
    return (
      <aside
        className={noticeClasses}
        aria-labelledby={headingId}
        data-testid="analytics-consent-unavailable"
      >
        <Card className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 id={headingId} className="text-lg font-semibold text-foreground">
              {t('analytics.consent.notice.heading')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('analytics.consent.notice.unavailable')}
            </p>
          </div>
          <ConsentSaveError error={saveError} />
          <div className={actionClasses}>
            <Button onClick={() => void consent.refresh()} aria-describedby={headingId}>
              {t('analytics.consent.notice.retry')}
            </Button>
          </div>
        </Card>
      </aside>
    );
  }

  if (
    resolution.kind !== 'ready' ||
    resolution.current.status !== 'unknown' ||
    initialNoticeDismissed
  ) {
    return null;
  }

  const saving = savingChoice !== null;
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !saving) {
          dismissInitialNotice();
        }
      }}
    >
      <Dialog.Content
        variant="bottom"
        aria-labelledby={headingId}
        data-testid="analytics-consent-notice"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Dialog.Title id={headingId}>{t('analytics.consent.notice.heading')}</Dialog.Title>
            <Dialog.Description className="whitespace-pre-line">
              {t('analytics.consent.notice.body')}
            </Dialog.Description>
          </div>
          <Dialog.Close
            aria-label={t('analytics.consent.notice.dismiss')}
            className={closeButtonClasses}
            disabled={saving}
          >
            <CloseIcon aria-hidden="true" className="h-5 w-5" />
          </Dialog.Close>
        </div>
        <ConsentSaveError error={saveError} className="mt-3" />
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            variant="outline"
            className="w-full"
            disabled={saving}
            onClick={() => void choose('accepted')}
            aria-describedby={headingId}
          >
            {savingChoice === 'accepted'
              ? t('analytics.consent.notice.saving')
              : t('analytics.consent.notice.accept')}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={saving}
            onClick={() => void choose('refused')}
            aria-describedby={headingId}
          >
            {savingChoice === 'refused'
              ? t('analytics.consent.notice.saving')
              : t('analytics.consent.notice.refuse')}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
